import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { fetchAllWricefCatalog } from "@/api/wricef-catalog";
import { formatCount } from "@/lib/format";
import type { WricefCatalogItem } from "@/types/wricef";

import type { DiagnosticTabProps } from "../DiagnosticSection";
import { cleanCoreDestination, complexityHighlights } from "../diagnosticData";
import { makeExtensionTargetMeta, makeWricefTypeMeta } from "../diagnosticMeta";
import { FilterButton } from "../orgStructure/FilterButton";
import { NO_VALUE } from "../sectionState";
import type { T } from "../acts/environment/i18nTypes";

/** O card branco das telas de detalhe, o mesmo do WRICEF e dos gaps. */
const CARD = "overflow-hidden rounded-lg border border-[#e3e8ef] bg-white";
const HEAD = "px-3 py-2 font-mono text-[9px] font-medium uppercase tracking-[0.08em] text-[#7b8799]";
const CELL = "px-3 py-2 align-top text-[12px] text-[#08254c]";

/** O alvo indefinido não entra: é o mesmo corte do número do card (`complexityHighlights`). */
const UNKNOWN_TARGET = "_UNKNOWN";
const REQUIRES_EXTENSION = "requires_extension";

/**
 * Os destinos com os nomes do card "Destino do código Z sob Clean Core". As chaves
 * são as que o catálogo grava; `standard_grow` é o apelido das runs antigas.
 */
function destinationLabel(key: string | null, t: T): string {
  switch (key) {
    case "undecided":
      return t("Undecided");
    case REQUIRES_EXTENSION:
      return t("Needs extension");
    case "standard_grow_equivalent":
    case "standard_grow":
      return t("Covered by the standard");
    case "retire":
      return t("Discontinue (unused)");
    default:
      return key ?? NO_VALUE;
  }
}

/** O catálogo inteiro do projeto, a mesma fonte da aba WRICEF do Architect. */
function useCatalog(projectId: string | undefined) {
  return useQuery({
    queryKey: ["diagnostic-catalog-objects", projectId],
    queryFn: () => fetchAllWricefCatalog(projectId!, {}),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

function Loading() {
  const { t } = useTranslation();
  return <p className="text-[13px] text-[#596b85]">{t("Loading…")}</p>;
}

function LoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]">
      {t("Could not load the objects.")}
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md border border-current px-3 py-1 text-[12px] font-semibold"
      >
        {t("Try again")}
      </button>
    </div>
  );
}

/** A tabela dos objetos, com a coluna do recorte que a tela usa (destino ou alvo). */
function ObjectsTable({
  rows,
  testId,
  lastColumn,
  lastValue,
}: {
  rows: WricefCatalogItem[];
  testId: string;
  lastColumn: string;
  lastValue: (row: WricefCatalogItem) => string;
}) {
  const { t } = useTranslation();
  const typeMeta = makeWricefTypeMeta(t) as Record<string, { label: string } | undefined>;
  return (
    <div className="overflow-x-auto">
      <table data-testid={testId} className="w-full min-w-[720px] border-collapse">
        <thead>
          <tr className="border-b border-[#e8ebf0] bg-[#f7f9fc] text-left">
            <th className={HEAD}>{t("Object")}</th>
            <th className={HEAD}>{t("Type")}</th>
            <th className={HEAD}>{t("Module")}</th>
            <th className={HEAD}>{t("Description")}</th>
            <th className={HEAD}>{lastColumn}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-[#eef1f5] last:border-b-0">
              <td className={`${CELL} font-mono`}>
                {row.obj_name}
                <span className="ml-1 text-[10px] text-[#7b8799]">· {row.obj_type_tadir}</span>
              </td>
              <td className={CELL}>{typeMeta[row.wricef]?.label ?? row.wricef ?? NO_VALUE}</td>
              <td className={CELL}>{row.resolved_stream ?? NO_VALUE}</td>
              <td className={`${CELL} max-w-[420px] text-[#3c4b61]`}>
                {row.description_pt ?? NO_VALUE}
              </td>
              <td className={CELL}>{lastValue(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * "Objetos com engenharia" do Ato 2 (pedido do David em 19/10/2026): os objetos
 * que exigem extensão e já têm alvo, que é exatamente o que o número do card soma.
 * Filtro pelo alvo de extensão. Sem valor financeiro.
 */
export function EngineeringObjectsDetail({ sections }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [params, setParams] = useSearchParams();
  const { data, isLoading, isError, refetch } = useCatalog(projectId);
  const targetMeta = makeExtensionTargetMeta(t) as Record<string, { label: string } | undefined>;
  const total = complexityHighlights(sections).catalogTotal;

  const rows = useMemo(
    () =>
      (data ?? []).filter(
        (row) =>
          row.disposition === REQUIRES_EXTENSION &&
          !!row.extensibility_target &&
          // O tipo não declara `_UNKNOWN`, mas a api pode mandar: o mesmo corte do card.
          (row.extensibility_target as string) !== UNKNOWN_TARGET,
      ),
    [data],
  );
  const targets = useMemo(
    () => Array.from(new Set(rows.map((row) => row.extensibility_target as string))).sort(),
    [rows],
  );
  const filter = params.get("target");
  const shown = filter ? rows.filter((row) => row.extensibility_target === filter) : rows;
  const choose = (value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set("target", value);
    else next.delete("target");
    setParams(next, { replace: true });
  };

  if (isLoading && !data) return <Loading />;
  if (isError || !data) return <LoadError onRetry={() => void refetch()} />;

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-[13px] leading-relaxed text-[#596b85]">
        {total !== undefined
          ? t("of {{total}}, only these need technical work", { total: formatCount(total) })
          : t("only these need technical work")}
      </p>
      <div className={CARD}>
        <div className="flex flex-wrap gap-2 border-b border-[#eef1f5] px-4 py-3">
          <FilterButton active={filter === null} onClick={() => choose(null)}>
            {t("All")} {formatCount(rows.length)}
          </FilterButton>
          {targets.map((target) => (
            <FilterButton key={target} active={filter === target} onClick={() => choose(target)}>
              {targetMeta[target]?.label ?? target}{" "}
              {formatCount(rows.filter((row) => row.extensibility_target === target).length)}
            </FilterButton>
          ))}
        </div>
        <ObjectsTable
          rows={shown}
          testId="engineering-objects-list"
          lastColumn={t("Extension target")}
          lastValue={(row) =>
            targetMeta[row.extensibility_target as string]?.label ??
            row.extensibility_target ??
            NO_VALUE
          }
        />
      </div>
    </div>
  );
}

/**
 * "Destino do código Z sob Clean Core" do Ato 2 (pedido do David em 19/10/2026):
 * cada objeto customizado com o destino que o card desenha, filtrável pelo
 * destino. A legenda do card abre esta tela já filtrada (`?destination=`).
 */
export function CleanCoreDestinationDetail({ sections }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [params, setParams] = useSearchParams();
  const { data, isLoading, isError, refetch } = useCatalog(projectId);
  const total = cleanCoreDestination(sections).total;

  const rows = useMemo(() => data ?? [], [data]);
  const destinations = useMemo(
    () => Array.from(new Set(rows.map((row) => row.disposition ?? "undecided"))).sort(),
    [rows],
  );
  const filter = params.get("destination");
  const shown = filter
    ? rows.filter((row) => (row.disposition ?? "undecided") === filter)
    : rows;
  const choose = (value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set("destination", value);
    else next.delete("destination");
    setParams(next, { replace: true });
  };

  if (isLoading && !data) return <Loading />;
  if (isError || !data) return <LoadError onRetry={() => void refetch()} />;

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-[13px] leading-relaxed text-[#596b85]">
        {total != null
          ? t("For each of the {{total}} customised objects", { total: formatCount(total) })
          : t("For each customised object")}
      </p>
      <div className={CARD}>
        <div className="flex flex-wrap gap-2 border-b border-[#eef1f5] px-4 py-3">
          <FilterButton active={filter === null} onClick={() => choose(null)}>
            {t("All")} {formatCount(rows.length)}
          </FilterButton>
          {destinations.map((key) => (
            <FilterButton key={key} active={filter === key} onClick={() => choose(key)}>
              {destinationLabel(key, t)}{" "}
              {formatCount(rows.filter((row) => (row.disposition ?? "undecided") === key).length)}
            </FilterButton>
          ))}
        </div>
        <ObjectsTable
          rows={shown}
          testId="clean-core-destination-list"
          lastColumn={t("Destination")}
          lastValue={(row) => destinationLabel(row.disposition ?? "undecided", t)}
        />
      </div>
    </div>
  );
}
